import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLearner } from '../../hooks/useLearner';
import { useChildCharacters } from '../../hooks/useChildCharacters';
import { usePractice } from '../../hooks/usePractice';
import { useDelight } from '../../contexts/DelightContext';
import { SessionAccProvider, useSessionAccumulator } from '../../contexts/SessionAccumulator';
import { resetVariantEngine } from '../../components/delight/MicroVariantEngine';
import SetupScreen from './SetupScreen';
import GameplayScreen from './GameplayScreen';
import RoundCompleteScreen from './RoundCompleteScreen';
import PracticeSummaryPage from './PracticeSummaryPage';
import s from './PracticePage.module.css';

const CHARS_PER_ROUND = 5;

function PracticeInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const seedChars = location.state?.seedChars || null;
  const autoStart = location.state?.autoStart === true;
  const { data: activeLearner } = useLearner();
  const learnerId = activeLearner?.id;
  const { data: characters, isLoading: charsLoading } = useChildCharacters(learnerId);
  const { recordCharacter, finishSession, resetSession, getResults } = usePractice(learnerId);
  const { enqueueDelight } = useDelight();
  const accumulator = useSessionAccumulator();

  const [view, setView] = useState('setup');
  const [pool, setPool] = useState([]);
  const [roundIndex, setRoundIndex] = useState(0);
  const [roundResults, setRoundResults] = useState([]);

  const handleStartSession = useCallback((selectedChars) => {
    const shuffled = [...selectedChars].sort(() => Math.random() - 0.5);
    setPool(shuffled);
    setRoundIndex(0);
    setRoundResults([]);
    resetSession();
    accumulator.startSession();
    resetVariantEngine();
    setView('gameplay');
  }, [resetSession, accumulator]);

  useEffect(() => {
    if (autoStart && seedChars && seedChars.length > 0 && view === 'setup') {
      handleStartSession(seedChars);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoundComplete = useCallback((results) => {
    setRoundResults(results);
    setView('round-end');
  }, []);

  const handleContinue = useCallback(() => {
    setRoundIndex((prev) => prev + 1);
    setRoundResults([]);
    setView('gameplay');
  }, []);

  const handleFinishSession = useCallback(async () => {
    try {
      const response = await finishSession();
      if (response?.progression) {
        enqueueDelight(response.progression);
      }
    } catch (err) {
      console.error('finishSession failed:', err);
    }
    setView('summary');
  }, [finishSession, enqueueDelight]);

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const roundStart = roundIndex * CHARS_PER_ROUND;
  const roundChars = pool.slice(roundStart, roundStart + CHARS_PER_ROUND);
  const hasMoreCharacters = roundStart + CHARS_PER_ROUND < pool.length;

  if (charsLoading) {
    return <div className={s.loading}>{t('practice.loadingCharacters')}</div>;
  }

  return (
    <div className={s.container}>
      {view === 'setup' && (
        <SetupScreen
          characters={characters || []}
          onStart={handleStartSession}
          onBack={() => navigate('/')}
          initialCustomChars={seedChars}
        />
      )}

      {view === 'gameplay' && (
        <GameplayScreen
          characters={roundChars}
          roundNumber={roundIndex + 1}
          recordCharacter={recordCharacter}
          enqueueDelight={enqueueDelight}
          onRoundComplete={handleRoundComplete}
          onQuit={handleFinishSession}
        />
      )}

      {view === 'round-end' && (
        <RoundCompleteScreen
          roundNumber={roundIndex + 1}
          xpEarned={roundResults.reduce((sum, r) => sum + (r.xp || 0), 0)}
          characters={roundResults}
          hasNextRound={hasMoreCharacters}
          onStartNextRound={handleContinue}
          onSeeSummary={handleFinishSession}
        />
      )}

      {view === 'summary' && (
        <PracticeSummaryPage
          results={getResults()}
          onGoHome={handleGoHome}
        />
      )}
    </div>
  );
}

export default function PracticePage() {
  return (
    <SessionAccProvider>
      <PracticeInner />
    </SessionAccProvider>
  );
}
